import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Clock, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { useAuth } from '@/lib/AuthContext'
import { useCommunityTime } from '@/lib/hooks/useCommunityTime'
import type { RetentionPreview } from '@/lib/sdk-official/resources/spaces'
import type { RetentionModule } from './retentionUtils'
import type { SetupConfirmProps } from '@/modules/types'

/* F-203a: the "Prazo de guarda" field and its confirmation; the rules live in `retentionUtils.ts`. */

interface FieldProps {
  module: RetentionModule
  value: unknown
  /** A shortening the space stores that still waits for its date (SEC-329). */
  pending: { months: number; at: string } | null
  onChange: (months: number | undefined) => void
  error?: string | null
  disabled?: boolean
}

/**
 * "Prazo de guarda (meses)". Owner and admin only (the preview and the rule are
 * theirs); for anyone else nothing is drawn and the stored value travels untouched.
 */
export const RetentionField = ({ module, value, pending, onChange, error, disabled = false }: FieldProps) => {
  const { t } = useTranslation('common')
  const { hasRole } = useAuth()
  const { date } = useCommunityTime()
  if (!hasRole(['owner', 'admin'])) return null
  const id = `${module}-retention-months`

  return (
    <div className="space-y-2 border-t pt-5" data-testid="retention-field">
      <label htmlFor={id} className="text-sm font-medium text-foreground">{t('retention.label')}</label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={typeof value === 'number' && !Number.isNaN(value) ? value : ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' ? Number.NaN : Number(e.target.value))}
        className={cn('max-w-[12rem]', error && 'border-danger')}
        aria-invalid={Boolean(error)}
        aria-describedby={`${id}-hint`}
      />
      {error
        ? <p id={`${id}-hint`} role="alert" className="text-xs text-danger-text">{error}</p>
        : <p id={`${id}-hint`} className="text-xs text-muted-foreground">{t(`retention.hint.${module}`)}</p>}
      {pending && (
        <p className="flex items-start gap-1.5 rounded-lg bg-warning-surface p-3 text-xs text-warning-text" data-testid="retention-pending">
          <Clock className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden />
          <span>{t('retention.pending', { count: pending.months, date: date(pending.at) })}</span>
        </p>
      )}
    </div>
  )
}

const COUNT_KEYS: Record<RetentionModule, Array<keyof RetentionPreview['counts']>> = {
  attendance: ['guestVisits', 'visitorInvitations'],
  parcels: ['parcels', 'parcelPhotos', 'parcelAuditLogs']
}

/**
 * The confirmation before a new retention period is saved: what the daily
 * routine would anonymize today with it (`GET /spaces/{id}/retention-preview`),
 * and, for a shorter period, the date it starts to apply.
 */
export const RetentionConfirmDialog = ({ module, spaceId, config, open, onConfirm, onCancel }: SetupConfirmProps & { module: RetentionModule }) => {
  const { t } = useTranslation('common')
  const { date } = useCommunityTime()
  const months = (config as { retentionMonths?: number } | null)?.retentionMonths as number
  const [preview, setPreview] = React.useState<RetentionPreview | null>(null)
  const [failed, setFailed] = React.useState(false)
  const [attempt, setAttempt] = React.useState(0)

  React.useEffect(() => {
    if (!open) return
    let alive = true
    setPreview(null)
    setFailed(false)
    sdk.spaces.retentionPreview(spaceId, months)
      .then((read) => { if (alive) setPreview(read) })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [open, spaceId, months, attempt])

  const shorter = preview ? preview.months < preview.currentMonths : false
  const counts = preview ? COUNT_KEYS[module].map((key) => ({ key, count: preview.counts[key] ?? 0 })) : []
  const nothing = counts.every(({ count }) => count === 0)

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel() }}>
      <DialogContent className="max-w-md" data-testid="retention-confirm">
        <DialogHeader>
          <DialogTitle>{t('retention.confirm.title')}</DialogTitle>
          <DialogDescription>
            {!preview
              ? t('retention.confirm.loading')
              : preview.months === preview.currentMonths
                /* Back to the period in force while a shortening waits: the shortening is dropped. */
                ? t('retention.confirm.keep', { count: preview.months })
                : t('retention.confirm.change', { from: preview.currentMonths, to: preview.months })}
          </DialogDescription>
        </DialogHeader>

        {failed && (
          <div className="space-y-2">
            <p role="alert" className="text-sm text-danger-text">{t('retention.confirm.failed')}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((n) => n + 1)}>{t('retry')}</Button>
          </div>
        )}

        {preview && (
          <div className="space-y-3 text-sm text-foreground">
            {nothing
              ? <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />{t('retention.confirm.none')}</p>
              : (
                <div className="space-y-1.5">
                  <p>{t('retention.confirm.intro')}</p>
                  <ul className="space-y-1 rounded-lg bg-surface-muted p-3" data-testid="retention-counts">
                    {counts.map(({ key, count }) => (
                      <li key={key} className="flex justify-between gap-4">
                        <span>{t(`retention.counts.${key}`, { count })}</span>
                        <span className="font-semibold tabular-nums text-foreground">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            {shorter
              ? (
                <p className="flex items-start gap-2 rounded-lg bg-warning-surface p-3 text-warning-text" data-testid="retention-grace">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
                  <span>{t('retention.confirm.grace', { date: date(preview.effectiveAt), count: preview.currentMonths })}</span>
                </p>
              )
              : <p className="text-muted-foreground">{t('retention.confirm.now')}</p>}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>{t('cancel')}</Button>
          <Button type="button" variant="secondary" disabled={!preview} onClick={onConfirm} data-testid="retention-confirm-save">
            {t('retention.confirm.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The confirmation of an Attendance space (`ModuleSetup.confirmSave`). */
export const AttendanceRetentionConfirm = (props: SetupConfirmProps) => <RetentionConfirmDialog module="attendance" {...props} />

/** The confirmation of a Parcels space (`ModuleSetup.confirmSave`). */
export const ParcelsRetentionConfirm = (props: SetupConfirmProps) => <RetentionConfirmDialog module="parcels" {...props} />
